/**
 * What the browser can say about the device (decision 102): the system and the browser always,
 * the model where User-Agent Client Hints give it (Android Chrome), memory and cores where the
 * browser reports them. Nothing leaves the page here; the test screen shows it and the user sends
 * it with a result. No user-agent string is ever sent (#489).
 */
export interface DetectedDevice {
  readonly os: string;
  readonly osVersion?: string;
  readonly browser: string;
  readonly browserVersion?: string;
  /** The phone's model, when the browser says it. */
  readonly model?: string;
  readonly mobile: boolean;
  readonly memoryGb?: number;
  readonly cores?: number;
  readonly screen: { readonly width: number; readonly height: number; readonly dpr: number };
}

interface UaBrand {
  readonly brand: string;
  readonly version: string;
}

interface UaData {
  readonly mobile?: boolean;
  readonly platform?: string;
  getHighEntropyValues?(hints: string[]): Promise<{
    platformVersion?: string;
    model?: string;
    fullVersionList?: UaBrand[];
  }>;
}

type NavigatorLike = Pick<Navigator, 'userAgent' | 'hardwareConcurrency'> & {
  readonly userAgentData?: UaData;
  readonly deviceMemory?: number;
};

type ScreenLike = {
  readonly screen: { width: number; height: number };
  readonly devicePixelRatio: number;
};

type Reading = {
  os: string;
  osVersion: string | undefined;
  browser: string;
  browserVersion: string | undefined;
  model: string | undefined;
  mobile: boolean;
};

/** The high-entropy values read over the user agent's own reading. */
function applyHints(
  hints: UaData,
  values: Awaited<ReturnType<NonNullable<UaData['getHighEntropyValues']>>>,
  from: Reading,
): Reading {
  const next = { ...from };
  if (hints.platform) next.os = hints.platform === 'macOS' ? 'macOS' : hints.platform;
  if (values.platformVersion) next.osVersion = majorOf(next.os, values.platformVersion);
  if (values.model) next.model = values.model.trim() || undefined;
  const brand = pickBrand(values.fullVersionList ?? []);
  if (brand) {
    next.browser = brand.name;
    next.browserVersion = brand.version.split('.')[0];
  }
  next.mobile = hints.mobile ?? next.mobile;
  return next;
}

export async function detectDevice(
  nav: NavigatorLike = navigator,
  win: ScreenLike = window,
): Promise<DetectedDevice> {
  const fromUa = parseUserAgent(nav.userAgent);
  let { os, osVersion, browser, browserVersion } = fromUa;
  let model: string | undefined;
  let mobile = fromUa.mobile;

  const hints = nav.userAgentData;
  if (hints?.getHighEntropyValues !== undefined) {
    try {
      const values = await hints.getHighEntropyValues([
        'platformVersion',
        'model',
        'fullVersionList',
      ]);
      ({ os, osVersion, browser, browserVersion, model, mobile } = applyHints(hints, values, {
        os,
        osVersion,
        browser,
        browserVersion,
        model,
        mobile,
      }));
    } catch {
      // The user agent's reading stands.
    }
  }

  return {
    os,
    ...(osVersion ? { osVersion } : {}),
    browser,
    ...(browserVersion ? { browserVersion } : {}),
    ...(model ? { model } : {}),
    mobile,
    ...(typeof nav.deviceMemory === 'number' ? { memoryGb: nav.deviceMemory } : {}),
    ...(nav.hardwareConcurrency ? { cores: nav.hardwareConcurrency } : {}),
    screen: {
      width: win.screen.width,
      height: win.screen.height,
      dpr: Math.round(win.devicePixelRatio * 100) / 100,
    },
  };
}

/** Only a phone without a model needs the one question on the screen («Che telefono è?»). */
export function needsModel(device: DetectedDevice): boolean {
  return device.mobile && device.model === undefined;
}

/** The two strings the API keeps (#489, ≤ 40 characters each). */
export function describeForRecord(
  device: DetectedDevice,
  model: string | undefined,
): { platform: string; browser: string } {
  const system = [device.os, device.osVersion].filter(Boolean).join(' ');
  const chosen = device.model ?? model?.trim();
  return {
    platform: (chosen ? `${system} · ${chosen}` : system).slice(0, 40),
    browser: [device.browser, device.browserVersion].filter(Boolean).join(' ').slice(0, 40),
  };
}

function majorOf(os: string, version: string): string {
  // Windows reports 13+ for Windows 11 through Client Hints.
  if (os === 'Windows') return Number(version.split('.')[0]) >= 13 ? '11' : '10';
  return version.split('.')[0] ?? version;
}

function pickBrand(brands: readonly UaBrand[]): { name: string; version: string } | undefined {
  const named = brands.filter((b) => !/not.?a.?brand/i.test(b.brand));
  const order = ['Microsoft Edge', 'Opera', 'Samsung Internet', 'Google Chrome', 'Chromium'];
  for (const wanted of order) {
    const hit = named.find((b) => b.brand === wanted);
    if (hit) return { name: wanted === 'Google Chrome' ? 'Chrome' : wanted, version: hit.version };
  }
  return undefined;
}

export function parseUserAgent(ua: string): {
  os: string;
  osVersion: string | undefined;
  browser: string;
  browserVersion: string | undefined;
  mobile: boolean;
} {
  let os = 'Altro';
  let osVersion: string | undefined;
  let mobile = /Mobi|Android|iPhone|iPad/.test(ua);
  let m: RegExpExecArray | null;
  if ((m = /iPhone OS (\d+)/.exec(ua)) !== null) {
    os = 'iOS';
    osVersion = m[1];
  } else if ((m = /iPad.*OS (\d+)/.exec(ua)) !== null) {
    os = 'iPadOS';
    osVersion = m[1];
  } else if ((m = /Android (\d+)/.exec(ua)) !== null) {
    os = 'Android';
    osVersion = m[1];
  } else if (/Windows NT/.test(ua)) {
    os = 'Windows';
  } else if (/Mac OS X/.test(ua)) {
    os = 'macOS';
    mobile = false;
  } else if (/Linux/.test(ua)) {
    os = 'Linux';
  }

  const browsers: [RegExp, string][] = [
    [/EdgA?\/(\d+)/, 'Microsoft Edge'],
    [/SamsungBrowser\/(\d+)/, 'Samsung Internet'],
    [/OPR\/(\d+)/, 'Opera'],
    [/CriOS\/(\d+)/, 'Chrome'],
    [/FxiOS\/(\d+)/, 'Firefox'],
    [/Firefox\/(\d+)/, 'Firefox'],
    [/Chrome\/(\d+)/, 'Chrome'],
    [/Version\/(\d+).*Safari/, 'Safari'],
  ];
  for (const [pattern, name] of browsers) {
    const hit = pattern.exec(ua);
    if (hit) return { os, osVersion, browser: name, browserVersion: hit[1], mobile };
  }
  return { os, osVersion, browser: 'Altro', browserVersion: undefined, mobile };
}
